import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * What a bulk write over questions did — the answer to both
 * {@link BulkQuestionStatusSchema} and {@link SectionVisibilityDefaultsSchema}.
 *
 * A summary rather than the affected rows, deliberately. `reorder` answers with
 * the whole section, which is exactly the shape paging breaks (see
 * `MoveQuestionSchema`); and the builder invalidates its page tag and refetches
 * anyway, so returning rows would buy a per-page options read and an
 * answer-count query for data the client throws away.
 *
 * There is no `changed` count, because it is `changedIds.length` — a second way
 * to say one thing is an invariant somebody has to keep true. The two numbers
 * here are not derivable from each other: `matched` is what the write **covered**
 * (the ids named, or the section's questions), `changedIds` names the rows that
 * actually differed, so a surface can say "42 of 57 changed" and a second
 * identical call can honestly report that it changed nothing.
 */
export const QuestionBulkResultSchema = z
  .object({
    /** How many questions the write covered. */
    matched: z.number().int().min(0),
    /** The ones whose stored values actually differed, and were written. */
    changedIds: z.array(UuidSchema),
  })
  .strict();

export type QuestionBulkResult = z.infer<typeof QuestionBulkResultSchema>;
